import { useCallback, useEffect, useState } from 'react';
import { usePdfViewerIdentity } from '../pdfViewer/usePdfViewerIdentity';
import { PatientsGrid } from './PatientsGrid';
import { patientTrackingApi } from './patientTrackingApi';
import type { CoveredEntityDto, OurPharmacyDto, PatientTrackingCsvImportConfig } from './patientTrackingTypes';

export interface PatientsPageProps {
  /** Optional override for the current user id (defaults to the signed-in profile object id). */
  currentUserId?: string;
  csvImport?: PatientTrackingCsvImportConfig;
  stateStorageKey?: string;
}

/**
 * Master Patients page: one record per real person, the FK target for every tracker issue.
 */
export function PatientsPage({ currentUserId, csvImport, stateStorageKey }: PatientsPageProps) {
  const identity = usePdfViewerIdentity(!currentUserId);
  const userId = currentUserId ?? identity.userId;
  const userName = identity.userName;
  const userAliases = identity.userAliases;

  const [coveredEntities, setCoveredEntities] = useState<CoveredEntityDto[]>([]);
  const [pharmacies, setPharmacies] = useState<OurPharmacyDto[]>([]);

  const loadCes = useCallback(async () => {
    try {
      setCoveredEntities(await patientTrackingApi.listCoveredEntities());
    } catch {
      setCoveredEntities([]);
    }
  }, []);

  const loadPharmacies = useCallback(async () => {
    try {
      setPharmacies(await patientTrackingApi.listPharmacies());
    } catch {
      setPharmacies([]);
    }
  }, []);

  useEffect(() => {
    void loadCes();
    void loadPharmacies();
  }, [loadCes, loadPharmacies]);

  return (
    <div className="ops-fill-page ops-fill-page--bare">
      <div className="ops-fill-page__grid-host">
        <PatientsGrid
          coveredEntities={coveredEntities}
          pharmacies={pharmacies}
          currentUserId={userId}
          currentUserName={userName}
          currentUserAliases={userAliases}
          csvImport={csvImport}
          stateStorageKey={stateStorageKey}
        />
      </div>
    </div>
  );
}
